import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { startOfDay } from "date-fns";
import { PeriodType } from "../../../common/data/service-proxies";
import { useIdlingFuelConsumption } from "../hooks/useIdleReportsHooks";
import { IdleReportsHookParams } from "../hooks/useIdleReportsHooks";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";
import { formatTooltipAxisWithUnit } from "../fuel-economy-report/chartUtils";
import ReactECharts from "echarts-for-react";
import "../fuel-economy-report/FuelEconomyReport.css";

const buildDayLabels = (
  from: Date,
  to: Date,
  periodType: PeriodType,
): string[] => {
  const result: string[] = [];
  const fromStart = startOfDay(from);
  const toStart = startOfDay(to);
  const oneDay = 24 * 60 * 60 * 1000;

  if (periodType === PeriodType.Weekly) {
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      result.push(
        new Date(d).toLocaleDateString("en-US", { weekday: "short" }),
      );
    }
  } else {
    const days: Date[] = [];
    for (let d = fromStart.getTime(); d <= toStart.getTime(); d += oneDay) {
      days.push(new Date(d));
    }
    const byWeek: { [bucket: string]: number } = {};
    days.forEach((d) => {
      const dayOfMonth = d.getDate();
      const bucket = `${Math.floor((dayOfMonth - 1) / 7) + 1}`;
      byWeek[bucket] = 1;
    });
    Object.keys(byWeek)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((b) => result.push(`W${b}`));
  }

  return result;
};

export const IdleConsumptionWidget: React.FC<IdleReportsHookParams> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();
  const { data, loading: isLoading, error } = useIdlingFuelConsumption({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;

  const chartOption = useMemo(() => {
    if (!data) return null;
    const labels = buildDayLabels(
      fromDate,
      toDate,
      periodType ?? PeriodType.Weekly,
    );
    const len = labels.length;
    if (len === 0) return null;
    const currentRaw = data.currentValues ?? [];
    const previousRaw = data.previousValues ?? [];
    const currentValues = Array.from({ length: len }, (_, i) =>
      i < currentRaw.length ? currentRaw[i] : 0,
    );
    const previousValues = Array.from({ length: len }, (_, i) =>
      i < previousRaw.length ? previousRaw[i] : 0,
    );
    const hasData =
      currentValues.some((v) => v > 0) || previousValues.some((v) => v > 0);
    if (!hasData) return null;

    const currentLabel = translate("fuelEconomyReport.currentPeriod", {
      _: "This week",
    });
    const previousLabel = translate("fuelEconomyReport.previousPeriod", {
      _: "Last week",
    });

    return {
      tooltip: {
        trigger: "axis",
        formatter: (params: unknown) =>
          formatTooltipAxisWithUnit(params, "gal"),
      },
      legend: {
        data: [currentLabel, previousLabel],
        bottom: 0,
        textStyle: { color: theme.palette.text.primary },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        top: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: labels,
        boundaryGap: false,
        axisLabel: { color: theme.palette.text.primary },
      },
      yAxis: {
        type: "value",
        min: 0,
        axisLabel: { color: theme.palette.text.primary },
        splitLine: { lineStyle: { color: theme.palette.divider } },
      },
      series: [
        {
          name: currentLabel,
          type: "line",
          data: currentValues,
          smooth: true,
          itemStyle: { color: "#a855f7" },
          symbol: "circle",
          symbolSize: 6,
        },
        {
          name: previousLabel,
          type: "line",
          data: previousValues,
          smooth: true,
          itemStyle: { color: "#14b8a6" },
          lineStyle: { type: "dashed" },
          symbol: "circle",
          symbolSize: 6,
        },
      ],
    };
  }, [
    data,
    fromDate,
    toDate,
    periodType,
    translate,
    theme.palette.text.primary,
    theme.palette.divider,
  ]);

  const hasData = useMemo(() => {
    if (!data) return false;
    const cur = data.currentValues?.length ?? 0;
    const prev = data.previousValues?.length ?? 0;
    return cur > 0 || prev > 0;
  }, [data]);

  return (
    <Card className="widget" style={{ height: "100%", width: "100%" }}>
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("idleReport.idleConsumption", {
            _: "Idle consumption (gal)",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !hasData ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", { _: "No data" })}
            </Typography>
          </Box>
        ) : chartOption ? (
          <Box flex={1} minHeight={300} style={{ minHeight: 0, width: "100%" }}>
            <ReactECharts
              option={chartOption}
              style={{ height: "100%", width: "100%", minHeight: 300 }}
            />
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default IdleConsumptionWidget;
